<script setup lang="ts">
import type {
  ColDef,
  GridReadyEvent,
  PostSortRowsParams,
  SortChangedEvent,
} from "ag-grid-community";
import { storeToRefs } from "pinia";
import { computed, nextTick, reactive, ref, watch } from "vue";
import { useRoute } from "vue-router";
import { toast } from "vue-sonner";
import AppGrid from "../../components/AppGrid.vue";
import {
  FITTABLE_DEFAULT_COL_DEF,
  GRID_SELECTION_COLUMN_DEF,
} from "../../components/composables/useGridColumnDefaults.ts";
import Dropdown from "../../components/Dropdown.vue";
import FormField from "../../components/FormField.vue";
import OKCancelModal from "../../components/OKCancelModal.vue";
import TestScenarioDocModal from "../../components/TestScenarioDocModal.vue";
import Textbox from "../../components/Textbox.vue";
import {
  ALGORITHM_OPTIONS,
  algorithmLabel,
} from "../../redistribution/models/AlgorithmType.ts";
import {
  DEMAND_SOURCE_OPTIONS,
  demandSourceLabel,
  usesTrailingSqlRosWindow,
} from "../../redistribution/models/DemandSourceType.ts";
import type { DropdownOption } from "../../redistribution/models/DropdownOption.ts";
import type {
  PurchaseOrder,
  RedistributionRequestPurchaseOrder,
} from "../../redistribution/models/PurchaseOrder.js";
import type { Sku } from "../../redistribution/models/Sku.js";
import { redistributeSkusByRequestBody } from "../../services/apiClient.ts";
import {
  getSkuForecasts,
  runRedistributionAlgorithm,
} from "../../services/purchaseOrderService.ts";
import { useAppStore } from "../../stores/app.ts";
import { useModalStore } from "../../stores/modalStore.ts";
import { useRedistributionQueueStore } from "../../stores/redistributionQueueStore.ts";
import { useRedistributionStore } from "../../stores/redistributionStore.ts";
import {
  calculateDaysOut,
  formatCalendarDateForDisplay,
  formatUsDate,
} from "../../utils/dateFormatting.ts";
import { clonePurchaseOrders } from "../../utils/objectCloning.ts";
import { resolveTestDataDates } from "../../utils/testDataDateResolver.ts";
import { getWarehouseDisplay } from "../../utils/warehouseDisplay.ts";
import { getWarehouseKey } from "../../utils/warehouseKeys.ts";
import { useGridRowManagement } from "./composables/useGridRowManagement.ts";
import CustomRequestBodyModal from "./CustomRequestBodyModal.vue";
import DetailsColumnRenderer from "./DetailsColumnRenderer.vue";
import RedistributionRunActions from "./RedistributionRunActions.vue";
import SelectedPurchaseOrdersPanel from "./SelectedPurchaseOrdersPanel.vue";

import PoDetailRenderer from "../results/PoDetailRenderer.vue";
import { SUPPORTED_RATE_OF_SALE_WINDOWS } from "../results/RateOfSaleResolver.ts";
import {
  calculateUrgency,
  compareUrgencyCounts,
  computeDaysToStockout,
  getDailyDemandForRateOfSale,
  summarizeUrgencyCounts,
  type UrgencyCounts,
} from "../results/resultHelpers.ts";
import {
  useGridSelection,
  type SelectionRowLike,
} from "./composables/useGridSelection.ts";
import { TEST_SCENARIOS } from "./testScenarios.ts";
const ROW_HEIGHT = 37;
const DETAIL_ROW_HEIGHT = 32;
/** Tune this value to change the item-count threshold for the large-selection confirmation dialog. */
const LARGE_ITEM_SELECTION_THRESHOLD = 1000;
const MAX_DETAIL_VISIBLE_ROWS = 5;
const route = useRoute();
const isTestMode = computed(() => route.query.test === "1");

const { setTraceLog, setLastCustomRequestBody } = useRedistributionStore();
const { lastCustomRequestBody } = storeToRefs(useRedistributionStore());
const appStore = useAppStore();
const modalStore = useModalStore();
const queueStore = useRedistributionQueueStore();

const {
  searching,
  poCube,
  rateOfSale,
  algorithmType,
  demandSource,
  availableDemandSources,
  selectedTestFile,
  searchResultsSortState,
  searchResults,
  poSearchResultsContext,
  lastSearchTypeLabel,
  activeSearchLoadingMessage,
  activeSearchNoResultsMessage,
  warehouseOptions,
} = storeToRefs(appStore);

// The five ROS windows exposed in the UI dropdown (all are supported by the API validator).
const ROS_OPTIONS: DropdownOption[] = [
  { label: "30 Days", value: "30" },
  { label: "60 Days", value: "60" },
  { label: "90 Days", value: "90" },
];

function normalizeRateOfSaleDays(value: string | number | undefined): number {
  const numericValue = Number(value ?? 30);
  return SUPPORTED_RATE_OF_SALE_WINDOWS.includes(numericValue)
    ? numericValue
    : 30;
}

// The slider is a SINGLE value bound directly to the store's `rateOfSale`
// (stored as a string). No lower-bound range is tracked anymore.
const selectedRateOfSaleDays = computed<number>({
  get: () => normalizeRateOfSaleDays(rateOfSale.value),
  set: (nextRateOfSaleDays) => {
    const nextValue = String(normalizeRateOfSaleDays(nextRateOfSaleDays));
    if (rateOfSale.value !== nextValue) {
      rateOfSale.value = nextValue;
    }
  },
});

type ResultsFilter = "all" | "cutting-date" | "related";

const resultsFilter = ref<ResultsFilter>("all");
const testDataLoading = ref(false);
const testDataError = ref<string | undefined>(undefined);
const testDocLoading = ref(false);

const testScenarioOptions = computed<DropdownOption[]>(() =>
  TEST_SCENARIOS.map((scenario) => ({
    label: scenario.label,
    value: scenario.filename,
    metadata: {
      category: scenario.category,
    },
  }))
);

const getTestScenarioCategory = (option: DropdownOption): string => {
  const category = option.metadata?.category;
  return typeof category === "string" ? category : "Other";
};

async function showTestScenarioDoc(): Promise<void> {
  const filename = selectedTestFile.value;
  if (!filename) return;
  const mdFilename = filename.replace(/\.json$/i, ".md");
  const entry = TEST_SCENARIOS.find((f) => f.filename === filename);
  const title = entry?.label ?? mdFilename;
  testDocLoading.value = true;
  try {
    const response = await fetch(`/test-data/${mdFilename}`);
    if (!response.ok) throw new Error(`Failed to load: ${response.statusText}`);
    const markdown = await response.text();
    modalStore.show(TestScenarioDocModal, {
      width: 760,
      height: 600,
      draggable: true,
      enableResizing: true,
      closeOnEscapeKeyPressed: true,
      componentProps: { title, markdown },
    });
  } catch {
    // silently ignore — the button is only enabled when a file is selected,
    // so a fetch failure is unlikely; a network error should not crash the UI.
  } finally {
    testDocLoading.value = false;
  }
}

watch(selectedTestFile, async (filename) => {
  if (!filename) return;
  testDataLoading.value = true;
  testDataError.value = undefined;
  try {
    const response = await fetch(`/test-data/${filename}`);
    if (!response.ok) throw new Error(`Failed to load: ${response.statusText}`);
    const rawText = await response.text();
    const resolvedText = resolveTestDataDates(rawText);
    const data = JSON.parse(resolvedText) as {
      label: string;
      description: string;
      purchaseOrders: PurchaseOrder[];
    };
    appStore.setSearchResults(data.purchaseOrders);
    await nextTick();
    gridApi.value?.selectAll();
  } catch (error) {
    testDataError.value =
      error instanceof Error ? error.message : "Failed to load test data";
  } finally {
    testDataLoading.value = false;
  }
});

interface PurchaseOrderRow {
  _isDetail?: false;
  rowKey: string;
  resultSource: "searched-po" | "cutting-date" | "related";
  poNumber: string;
  shipDate: string;
  shipDateIso: string;
  estArrival: string;
  estArrivalIso: string;
  daysOut: number;
  totalItems: number;
  uniqueSkus: number;
  criticalSkus: number;
  warehouse: number;
  warehouseDisplay: string;
  // division: string;
  status: string;
  cubeTotal: number;
  details: string;
  urgencyCounts: UrgencyCounts;
  vendorCode: string;
  po: PurchaseOrder;
}

interface DetailRow {
  _isDetail: true;
  parentRowKey: string;
  resultSource: "searched-po" | "cutting-date" | "related";
  parentPoNumber: string;
  skus: Sku[];
}

type GridRow = PurchaseOrderRow | DetailRow;

const {
  expandedPOs,
  gridApi,
  resetExpandedRows,
  isFullWidthRow,
  getRowId,
  getRowHeight,
  toggleDetail,
} = useGridRowManagement<GridRow>({
  rowHeight: ROW_HEIGHT,
  detailRowHeight: DETAIL_ROW_HEIGHT,
  maxDetailVisibleRows: MAX_DETAIL_VISIBLE_ROWS,
  detailToggleColumnId: "details",
});

watch(searching, () => {
  resetExpandedRows();
});

watch(selectedRateOfSaleDays, () => {
  void nextTick(() => {
    if (gridApi.value && !gridApi.value.isDestroyed()) {
      gridApi.value.setGridOption("rowData", rowData.value);
      gridApi.value.refreshClientSideRowModel("sort");
      gridApi.value.redrawRows();
    }
  });
});

function countCriticalUniqueSkus(
  skus: Sku[],
  selectedRateOfSale?: string | number
): number {
  const criticalSkuNumbers = new Set<string>();

  for (const sku of skus) {
    const dailyDemand = getDailyDemandForRateOfSale(sku, selectedRateOfSale);
    const daysToStockout = computeDaysToStockout(
      sku.forecastQuantityAvailable,
      dailyDemand
    );
    const urgency = calculateUrgency(daysToStockout, dailyDemand);
    if (urgency === 2) {
      criticalSkuNumbers.add(sku.skuNumber);
    }
  }

  return criticalSkuNumbers.size;
}

function calculateCubeTotal(skus: Sku[]): number {
  return skus.reduce((total, sku) => {
    return total + sku.cubeSize * sku.lineItemQuantity;
  }, 0);
}

function buildPurchaseOrderRow(
  po: PurchaseOrder,
  resultSource: "searched-po" | "cutting-date" | "related"
): PurchaseOrderRow {
  const poNumber = po.purchaseOrderNumber;
  return {
    rowKey: `${resultSource}:${poNumber}`,
    resultSource,
    poNumber,
    shipDate: formatCalendarDateForDisplay(po.shipDate),
    shipDateIso: po.shipDate,
    estArrival: formatUsDate(po.arrivalDate),
    estArrivalIso: po.arrivalDate,
    daysOut: calculateDaysOut(po.arrivalDate),
    totalItems: po.sumQuantities,
    cubeTotal: calculateCubeTotal(po.skus),
    uniqueSkus: po.countUniqueSkus,
    criticalSkus: countCriticalUniqueSkus(
      po.skus,
      selectedRateOfSaleDays.value
    ),
    warehouse: po.warehouse,
    warehouseDisplay: getWarehouseDisplay(
      po.division,
      po.warehouse,
      warehouseOptions.value
    ),
    // division: po.division,
    status: po.purchaseOrderStatus,
    details: "View Items ▾",
    urgencyCounts: summarizeUrgencyCounts(
      po.skus,
      selectedRateOfSaleDays.value
    ),
    vendorCode: po.vendorCode,
    po,
  };
}

const poRows = computed<PurchaseOrderRow[]>(() => {
  if (!searchResults.value || searchResults.value.length === 0) return [];
  const primaryResultSource: PurchaseOrderRow["resultSource"] =
    poSearchResultsContext.value.mode === "single-po-related"
      ? "searched-po"
      : "cutting-date";

  return searchResults.value.map((po) =>
    buildPurchaseOrderRow(po, primaryResultSource)
  );
});

const relatedRows = computed<PurchaseOrderRow[]>(() => {
  if (
    poSearchResultsContext.value.mode !== "single-po-related" ||
    poSearchResultsContext.value.relatedPurchaseOrders.length === 0
  ) {
    return [];
  }

  return poSearchResultsContext.value.relatedPurchaseOrders.map((po) =>
    buildPurchaseOrderRow(po, "related")
  );
});
const hasRelatedRows = computed(() => relatedRows.value.length > 0);
const showRelatedFeedback = computed(() => {
  return (
    !hasRelatedRows.value &&
    poSearchResultsContext.value.helperMessage !== undefined
  );
});
const relatedFeedbackTone = computed(() => {
  return poSearchResultsContext.value.helperMessageTone ?? "info";
});
const relatedFeedbackRole = computed(() => {
  return relatedFeedbackTone.value === "error" ? "alert" : "status";
});

const tooManyVendors = computed<boolean>(() => {
  if (!searchResults.value || searchResults.value.length === 0) {
    return false;
  }
  const vendorCodes = new Set(searchResults.value.map((po) => po.vendorCode));
  return vendorCodes.size > 1;
});

watch(tooManyVendors, (isTooMany) => {
  if (!isTooMany) return;
  if (modalStore.isOpen("too-many-vendors")) return;
  modalStore.show(OKCancelModal, {
    width: 440,
    height: 180,
    closeOnEscapeKeyPressed: true,
    dialogId: "too-many-vendors",
    componentProps: {
      title: "Multiple Vendors Detected",
      message:
        "Please modify the list of PO Numbers to only include POs from a single vendor.",
      okButtonText: "OK",
    },
  });
});

const rowData = computed<GridRow[]>(() => {
  if (tooManyVendors.value) return [];

  const showCuttingDate =
    resultsFilter.value === "all" || resultsFilter.value === "cutting-date";
  const showRelated =
    resultsFilter.value === "all" || resultsFilter.value === "related";

  const sourceRows: PurchaseOrderRow[] = [
    ...(showCuttingDate ? poRows.value : []),
    ...(showRelated ? relatedRows.value : []),
  ];

  const rows: GridRow[] = [];
  for (const po of sourceRows) {
    rows.push(po);
    if (expandedPOs.value.has(po.rowKey)) {
      rows.push({
        _isDetail: true,
        parentRowKey: po.rowKey,
        resultSource: po.resultSource,
        parentPoNumber: po.poNumber,
        skus: po.po.skus,
      });
    }
  }
  return rows;
});

const totalPOsFound = computed(() => poRows.value.length);
const totalRelatedFound = computed(() => relatedRows.value.length);
const hasFilterToggle = computed(() => hasRelatedRows.value);
const headerResultsCountLabel = computed(() => {
  if (totalPOsFound.value === 1) {
    return "1 PO found";
  }

  if (totalPOsFound.value === 0) {
    return "No POs found";
  }

  return `${String(totalPOsFound.value)} POs found`;
});

const headerSearchSummaryLabel = computed(() => {
  if (!lastSearchTypeLabel.value) {
    return headerResultsCountLabel.value;
  }

  return `${lastSearchTypeLabel.value} • ${headerResultsCountLabel.value}`;
});

const gridLoadingMessage = computed(() => {
  return searching.value
    ? (activeSearchLoadingMessage.value ?? "Searching...")
    : void 0;
});

const activeFilterLabel = computed(() => {
  switch (resultsFilter.value) {
    case "cutting-date": {
      return poSearchResultsContext.value.mode === "single-po-related"
        ? "Searched PO"
        : "Cutting Date";
    }
    case "related": {
      return "Related POs";
    }
    default: {
      return "All Results";
    }
  }
});

const primaryResultSetChipLabel = computed(() => {
  if (poSearchResultsContext.value.mode === "single-po-related")
    return "Searched PO Results";
  if (poSearchResultsContext.value.mode === "vendor-search") {
    const vName = poSearchResultsContext.value.vendorName;
    return vName ? `Vendor - ${vName}` : "Vendor Results";
  }
  return "Search Results";
});

const showInlineSearchLoadingBanner = computed(
  () => searching.value && rowData.value.length > 0
);

const inlineSearchLoadingMessage = computed(() =>
  poSearchResultsContext.value.mode === "single-po-related"
    ? "Loading related purchase orders..."
    : "Searching..."
);

function breakHeaderAfterFirstWord(headerName: string): string {
  const trimmedHeaderName = headerName.trim();
  const firstSpaceIndex = trimmedHeaderName.indexOf(" ");

  if (
    firstSpaceIndex <= 0 ||
    firstSpaceIndex === trimmedHeaderName.length - 1
  ) {
    return trimmedHeaderName;
  }

  const firstWord = trimmedHeaderName.slice(0, firstSpaceIndex);
  const remainder = trimmedHeaderName.slice(firstSpaceIndex + 1).trim();
  return `${firstWord}\n${remainder}`;
}

const columnDefs = ref<ColDef<PurchaseOrderRow>[]>([
  // {
  //   headerName: "RESULT SET",
  //   field: "resultSource",
  //   minWidth: 135,
  //   maxWidth: 150,
  //   filter: false,
  //   cellRenderer: (params: { value: PurchaseOrderRow["resultSource"] }) => {
  //     const isRelated = params.value === "related";
  //     const label = isRelated ? "Related" : primaryResultSetLabel.value;
  //     const cssClass = isRelated
  //       ? "result-source-badge result-source-badge--related"
  //       : "result-source-badge result-source-badge--primary";

  //     return `<span class="${cssClass}">${label}</span>`;
  //   },
  // },
  {
    headerName: breakHeaderAfterFirstWord("PO NUMBER"),
    field: "poNumber",
    minWidth: 85,
    cellRenderer: (params: { value: string }) =>
      params.value
        ? `<span class="select-text cursor-text">${params.value}</span>`
        : "",
  },
  {
    headerName: "WAREHOUSE",
    field: "warehouseDisplay",
    minWidth: 95,
    cellRenderer: (params: { data: PurchaseOrderRow }) => {
      const label = params.data.warehouseDisplay;
      return `<span class="results-panel-location-cell" title="${label}">${label}</span>`;
    },
  },
  // { headerName: "DIVISION", field: "division", minWidth: 110, filter: false },
  {
    headerName: breakHeaderAfterFirstWord("SHIP DATE"),
    field: "shipDate",
    minWidth: 85,
  },
  {
    headerName: breakHeaderAfterFirstWord("EST. ARRIVAL"),
    field: "estArrival",
    minWidth: 85,
  },
  {
    headerName: breakHeaderAfterFirstWord("DAYS OUT"),
    field: "daysOut",
    minWidth: 65,
  },
  {
    headerName: breakHeaderAfterFirstWord("ITEMS on PO"),
    field: "totalItems",
    minWidth: 72,
  },
  {
    headerName: breakHeaderAfterFirstWord("UNIQUE SKUS"),
    field: "uniqueSkus",
    minWidth: 72,
  },
  {
    headerName: breakHeaderAfterFirstWord("CRITICAL SKUS"),
    field: "criticalSkus",
    minWidth: 80,
  },
  {
    headerName: breakHeaderAfterFirstWord("PO CUBES"),
    field: "cubeTotal",
    minWidth: 72,
    valueFormatter: (params) => {
      const value = params.value as unknown;
      if (value == null) return "";
      // Ensure value is a number before calling toFixed
      if (typeof value === "number") {
        return value.toFixed(2);
      }
      const num = Number(value);
      return Number.isFinite(num) ? num.toFixed(2) : "";
    },
  },
  {
    headerName: "DETAILS",
    colId: "details",
    sort: "desc",
    headerClass: "search-results-details-header",
    filter: false,
    valueGetter: (parameters) => {
      const data = parameters.data;
      if (!data) return "";
      return expandedPOs.value.has(data.rowKey)
        ? "View Items ▴"
        : "View Items ▾";
    },
    cellRenderer: DetailsColumnRenderer,
    cellRendererParams: {
      onToggleDetail: (rowKey: string) => {
        toggleDetail(rowKey);
      },
    },
    cellClass: "search-results-details-cell",
    cellStyle: {
      color: "#2b5daa",
      cursor: "pointer",
      justifyContent: "center",
      textAlign: "center",
    },
    comparator: (_valueA, _valueB, nodeA, nodeB, isDescending) => {
      const leftRow = nodeA.data as PurchaseOrderRow | DetailRow | undefined;
      const rightRow = nodeB.data as PurchaseOrderRow | DetailRow | undefined;

      const leftCounts =
        leftRow && !("_isDetail" in leftRow && leftRow._isDetail)
          ? leftRow.urgencyCounts
          : undefined;
      const rightCounts =
        rightRow && !("_isDetail" in rightRow && rightRow._isDetail)
          ? rightRow.urgencyCounts
          : undefined;

      const urgencyComparison = compareUrgencyCounts(leftCounts, rightCounts);
      if (urgencyComparison !== 0) {
        return urgencyComparison;
      }

      const leftShipDate =
        leftRow && !("_isDetail" in leftRow && leftRow._isDetail)
          ? leftRow.shipDateIso
          : "";
      const rightShipDate =
        rightRow && !("_isDetail" in rightRow && rightRow._isDetail)
          ? rightRow.shipDateIso
          : "";
      const shipDateComparison = leftShipDate.localeCompare(rightShipDate);

      return isDescending ? -shipDateComparison : shipDateComparison;
    },
    minWidth: 110,
  },
]);

const rowSelection = {
  mode: "multiRow" as const,
  checkboxes: true,
  headerCheckbox: true,
  enableClickSelection: true,
  selectAll: "filtered" as const,
};

const selectionColumnDef: ColDef = GRID_SELECTION_COLUMN_DEF;

const defaultColDef: ColDef = FITTABLE_DEFAULT_COL_DEF;

function getSelectedRowItemCount(row: SelectionRowLike): number {
  if (Number.isFinite(row.totalItems)) {
    return row.totalItems;
  }

  const fallbackFromPurchaseOrder = row.po.sumQuantities;
  if (Number.isFinite(fallbackFromPurchaseOrder)) {
    return fallbackFromPurchaseOrder;
  }

  return 0;
}
const {
  selectedRows: selectedPOs,
  getSelectedPurchaseOrders,
  totalSelectedItems,
  onSelectionChanged,
  restoreSelections,
  removeFromSelection,
} = useGridSelection({
  getGridApi: () => gridApi.value,
  getStoredPoNumbers: () => appStore.searchSelectedPoNumbers,
  setStoredPoNumbers: appStore.setSearchSelectedPoNumbers,
  setStoredPurchaseOrders: appStore.setSearchSelectedPurchaseOrders,
  toSelectionRow: (row) => {
    if (row === undefined || row === null || typeof row !== "object") {
      return;
    }

    if ("_isDetail" in row && row._isDetail) {
      return;
    }

    return row as PurchaseOrderRow;
  },
  isDetailRow: (row) => {
    if (row === undefined || row === null || typeof row !== "object") {
      return false;
    }

    return "_isDetail" in row && row._isDetail === true;
  },
  getRowItemCount: getSelectedRowItemCount,
});

function restoreSortState(): void {
  if (!gridApi.value || searchResultsSortState.value.length === 0) {
    return;
  }

  gridApi.value.applyColumnState({
    state: searchResultsSortState.value,
    defaultState: { sort: null },
  });
}

function onSortChanged(event: SortChangedEvent<GridRow>): void {
  const sortState: {
    colId: string;
    sort: "asc" | "desc";
    sortIndex?: number | null;
  }[] = [];

  for (const state of event.api.getColumnState()) {
    if (state.sort !== "asc" && state.sort !== "desc") {
      continue;
    }

    sortState.push({
      colId: state.colId,
      sort: state.sort,
      sortIndex: state.sortIndex ?? null,
    });
  }

  appStore.setSearchResultsSortState(sortState);
}

function postSortRows(params: PostSortRowsParams<GridRow>): void {
  const parentNodes: typeof params.nodes = [];
  const detailByParent = new Map<string, (typeof params.nodes)[number]>();
  const primaryNodes: typeof params.nodes = [];
  const relatedNodes: typeof params.nodes = [];

  for (const node of params.nodes) {
    const data = node.data;
    if (data && "_isDetail" in data && data._isDetail) {
      detailByParent.set(data.parentRowKey, node);
      continue;
    }

    parentNodes.push(node);
  }

  params.nodes.length = 0;

  for (const node of parentNodes) {
    const data = node.data as PurchaseOrderRow | undefined;
    if (data?.resultSource === "related") {
      relatedNodes.push(node);
      continue;
    }
    primaryNodes.push(node);
  }

  for (const node of [...primaryNodes, ...relatedNodes]) {
    params.nodes.push(node);

    const data = node.data as PurchaseOrderRow | undefined;
    if (!data) {
      continue;
    }

    const detailNode = detailByParent.get(data.rowKey);
    if (detailNode) {
      params.nodes.push(detailNode);
    }
  }
}

watch(hasRelatedRows, (hasRelated) => {
  if (!hasRelated && resultsFilter.value === "related") {
    resultsFilter.value = "all";
  }
});

function onGridReady(event: GridReadyEvent<GridRow>): void {
  gridApi.value = event.api;
  void nextTick(() => {
    restoreSortState();
    restoreSelections();
  });
}

function toggleAllPOs(): void {
  if (!gridApi.value) return;
  if (tooManyVendors.value) return;

  let displayedRows = 0;
  gridApi.value.forEachNodeAfterFilterAndSort((node) => {
    const data = node.data;
    if (!data || ("_isDetail" in data && data._isDetail)) {
      return;
    }
    displayedRows += 1;
  });

  const selectedRows = gridApi.value.getSelectedRows().length;
  if (displayedRows > 0 && selectedRows === displayedRows) {
    gridApi.value.deselectAll();
    return;
  }
  gridApi.value.selectAll();
}

const isPreparingCustomRequestDialog = ref(false);
type RunCalculationPhase =
  | "idle"
  | "preparing-request"
  | "running-algorithm"
  | "finalizing-results";

interface AlgorithmRunState {
  isRunning: boolean;
  phase: RunCalculationPhase;
  error: string | undefined;
}

const runState = reactive<AlgorithmRunState>({
  isRunning: false,
  phase: "idle",
  error: undefined,
});

const isRunning = computed(() => runState.isRunning);
const algorithmError = computed(() => runState.error);
// Total is dynamic: 1 prepare step + 1 per selected algorithm + 1 finalize step.
// Currently algorithmType holds a single value, so this evaluates to 3.
const runCalculationStatus = computed(() => {
  if (!runState.isRunning || runState.phase === "idle") {
    return;
  }

  // 1 (prepare) + 1 (run the selected algorithm) + 1 (finalize)
  const totalCalculations = 3;

  const phaseIndexByPhase: Record<
    Exclude<RunCalculationPhase, "idle">,
    number
  > = {
    "preparing-request": 1,
    "running-algorithm": 2,
    "finalizing-results": 3,
  };
  const phaseLabelByPhase: Record<
    Exclude<RunCalculationPhase, "idle">,
    string
  > = {
    "preparing-request": "Preparing redistribution request",
    "running-algorithm": `Running: ${algorithmLabel(algorithmType.value)} · ${demandSourceLabel(demandSource.value)}`,
    "finalizing-results": "Finalizing redistribution results",
  };

  const phase = runState.phase;
  const currentCalculation = phaseIndexByPhase[phase];

  return {
    currentLabel: phaseLabelByPhase[phase],
    currentCalculation,
    totalCalculations,
    remainingCalculations: Math.max(0, totalCalculations - currentCalculation),
  };
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

// ── Algorithm options ──────────────────────────────────────────────────────
// Cascade rules (4 valid states):
//   [] none → run button disabled
//   [CriticalRisk]
//   [CriticalRisk, ModerateRisk]
//   [CriticalRisk, ModerateRisk, BalanceWos]
//
// Unchecking propagates DOWN: uncheck CriticalRisk → clears ModerateRisk + Wos
//                              uncheck ModerateRisk → clears Wos
// Checking propagates UP:   check ModerateRisk → forces CriticalRisk on
//                            check Wos → forces ModerateRisk + CriticalRisk on
interface AlgorithmOptionsState {
  criticalRisk: boolean;
  moderateRisk: boolean;
  balanceWos: boolean;
}

const algorithmOptions = reactive<AlgorithmOptionsState>({
  criticalRisk: true,
  moderateRisk: true,
  balanceWos: true,
});

function updateAlgorithmOption(
  key: keyof AlgorithmOptionsState,
  enabled: boolean
): void {
  algorithmOptions[key] = enabled;

  if (key === "criticalRisk" && !enabled) {
    algorithmOptions.moderateRisk = false;
    algorithmOptions.balanceWos = false;
    return;
  }

  if (key === "moderateRisk") {
    if (enabled) {
      algorithmOptions.criticalRisk = true;
      return;
    }

    algorithmOptions.balanceWos = false;
    return;
  }

  if (key === "balanceWos" && enabled) {
    algorithmOptions.moderateRisk = true;
    algorithmOptions.criticalRisk = true;
  }
}

function buildPreparedPurchaseOrders(): PurchaseOrder[] {
  return getSelectedPurchaseOrders().map((po) => ({
    ...po,
    skus: po.skus.map((sku) => {
      const dailyDemand = getDailyDemandForRateOfSale(
        sku,
        selectedRateOfSaleDays.value
      );
      const days = computeDaysToStockout(
        sku.forecastQuantityAvailable,
        dailyDemand
      );
      const urgency = calculateUrgency(days, dailyDemand);
      return { ...sku, urgency };
    }),
  }));
}

function stripDerivedPurchaseOrderFields(
  purchaseOrders: PurchaseOrder[]
): RedistributionRequestPurchaseOrder[] {
  return purchaseOrders.map((purchaseOrder) => {
    const sanitizedPurchaseOrder = {
      ...purchaseOrder,
    } as Record<string, unknown>;
    delete sanitizedPurchaseOrder.countUniqueSkus;
    delete sanitizedPurchaseOrder.sumQuantities;
    delete sanitizedPurchaseOrder.cubeTotal;
    delete sanitizedPurchaseOrder.calcArrivalDate;
    return sanitizedPurchaseOrder as RedistributionRequestPurchaseOrder;
  });
}

function buildAllowedReasons(purchaseOrders: PurchaseOrder[]): string[] {
  // Determine number of unique warehouses in selected POs
  const warehouseSet = new Set<string>();
  for (const po of purchaseOrders) {
    warehouseSet.add(getWarehouseKey(po.division, po.warehouse));
  }
  const warehouseCount = warehouseSet.size;

  // MakeRoom is always included (structural side-effect)
  const allowedReasons: string[] = ["MakeRoom"];

  if (warehouseCount === 1) {
    // Single warehouse: only allow CriticalRisk and ModerateRisk
    allowedReasons.unshift("CriticalRisk");
    if (algorithmOptions.moderateRisk) allowedReasons.push("ModerateRisk");
  } else {
    // Multiple warehouses: only allow BalanceWos
    allowedReasons.unshift("BalanceWos");
  }

  return allowedReasons;
}

function buildAvgWosWarehouses(purchaseOrders: PurchaseOrder[]): string[] {
  const avgWOSWarehouses: string[] = [];
  for (const po of purchaseOrders) {
    const warehouseKey = getWarehouseKey(po.division, po.warehouse);
    if (!avgWOSWarehouses.includes(warehouseKey)) {
      avgWOSWarehouses.push(warehouseKey);
    }
  }
  return avgWOSWarehouses;
}

const demandSourceOptions = computed(() =>
  DEMAND_SOURCE_OPTIONS.filter((option) =>
    availableDemandSources.value.includes(option.value)
  )
);

const rateOfSaleFieldLabel = computed(() =>
  usesTrailingSqlRosWindow(demandSource.value)
    ? "Rate of Sale (days)"
    : "Coverage horizon (days)"
);

interface RedistributionRequestPayload {
  purchaseOrders: PurchaseOrder[];
  rateOfSaleDays: number;
  poCube: number;
  algorithm: typeof algorithmType.value;
  demandSource: typeof demandSource.value;
  allowedReasons: string[];
  avgDaysOfStockBySku: Record<string, number>;
}

function toRedistributionRequestBody(
  requestPayload: RedistributionRequestPayload
): {
  purchaseOrders: RedistributionRequestPurchaseOrder[];
  rateOfSaleDays: number;
  poCube: number;
  algorithm: typeof algorithmType.value;
  demandSource: typeof demandSource.value;
  allowedReasons: string[];
  avgDaysOfStockBySku: Record<string, number>;
} {
  return {
    ...requestPayload,
    purchaseOrders: stripDerivedPurchaseOrderFields(
      requestPayload.purchaseOrders
    ),
  };
}

async function buildRedistributionRequestPayload(): Promise<RedistributionRequestPayload> {
  const parsedCube = Number(poCube.value);
  if (!Number.isFinite(parsedCube) || parsedCube <= 0) {
    throw new TypeError("PO Cube Size must be a positive number.");
  }

  const rateOfSaleDays = selectedRateOfSaleDays.value;
  const preparedPurchaseOrders = buildPreparedPurchaseOrders();
  const algorithm = algorithmType.value;
  const selectedDemandSource = demandSource.value;
  const allowedReasons = buildAllowedReasons(preparedPurchaseOrders);
  let avgDaysOfStockBySku: Record<string, number> = {};

  const hasAnySkus = preparedPurchaseOrders.some((po) => po.skus.length > 0);
  if (hasAnySkus) {
    const avgWOSWarehouses = buildAvgWosWarehouses(preparedPurchaseOrders);
    try {
      avgDaysOfStockBySku = await getSkuForecasts(
        avgWOSWarehouses,
        preparedPurchaseOrders,
        rateOfSaleDays
      );
    } catch {
      avgDaysOfStockBySku = {};
    }
  }

  return {
    purchaseOrders: preparedPurchaseOrders,
    rateOfSaleDays,
    poCube: parsedCube,
    algorithm,
    demandSource: selectedDemandSource,
    allowedReasons,
    avgDaysOfStockBySku,
  };
}

async function executeRedistributionRequest(args: {
  requestPayload: unknown;
  originals: PurchaseOrder[];
  requestBody?: string;
}): Promise<void> {
  const submission = await redistributeSkusByRequestBody(args.requestPayload);
  queueStore.enqueue(
    submission.requestId,
    submission.submittedAt,
    args.originals,
    args.requestBody
  );
  toast.info("Redistribution submitted", {
    description: "You'll be notified when results are ready.",
  });
}

function parseCustomRedistributionRequest(requestBodyText: string): {
  requestPayload: Record<string, unknown>;
  originals: PurchaseOrder[];
} {
  const parsedValue: unknown = JSON.parse(requestBodyText);
  if (!isRecord(parsedValue)) {
    throw new TypeError("Request body must be a JSON object.");
  }

  const purchaseOrdersValue = parsedValue.purchaseOrders;
  if (!Array.isArray(purchaseOrdersValue)) {
    throw new TypeError("Request body must include a purchaseOrders array.");
  }

  return {
    requestPayload: parsedValue,
    originals: clonePurchaseOrders(purchaseOrdersValue as PurchaseOrder[]),
  };
}

async function runCustomRequestBody(requestBodyText: string): Promise<void> {
  runState.error = undefined;

  if (!queueStore.canSubmit) {
    throw new Error(
      "Too many running requests. Please wait for one to complete."
    );
  }

  const parsed = parseCustomRedistributionRequest(requestBodyText);
  setLastCustomRequestBody(requestBodyText);
  await executeRedistributionRequest({
    requestPayload: parsed.requestPayload,
    originals: parsed.originals,
    requestBody: requestBodyText,
  });
}

async function openCustomRequestBodyDialog(): Promise<void> {
  if (runState.isRunning || isPreparingCustomRequestDialog.value) {
    return;
  }

  if (modalStore.isOpen("custom-request-body")) {
    return;
  }

  isPreparingCustomRequestDialog.value = true;

  runState.error = undefined;

  try {
    const hasSearchRows = rowData.value.some(
      (row) => !("_isDetail" in row && row._isDetail)
    );

    const hasEnoughSelected = hasSearchRows && selectedPOs.value.length >= 2;

    let initialRequestBody: string;
    let requestBodySource: string | undefined;

    if (hasEnoughSelected) {
      const requestPayload = await buildRedistributionRequestPayload();
      initialRequestBody = JSON.stringify(
        toRedistributionRequestBody(requestPayload),
        null,
        2
      );
      requestBodySource = "Request generated from currently selected POs";
    } else if (lastCustomRequestBody.value) {
      initialRequestBody = lastCustomRequestBody.value;
      requestBodySource = "Request generated from last run Redistribution";
    } else {
      initialRequestBody = "";
      requestBodySource = undefined;
    }

    modalStore.show(CustomRequestBodyModal, {
      width: 900,
      height: 700,
      draggable: true,
      enableResizing: true,
      closeOnEscapeKeyPressed: true,
      dialogId: "custom-request-body",
      componentProps: {
        title: "Run Custom Redistribution",
        initialRequestBody,
        requestBodySource,
        onRunRequestBody: runCustomRequestBody,
      },
    });
  } catch (error) {
    runState.error =
      error instanceof Error ? error.message : "Failed to build request body.";
  } finally {
    isPreparingCustomRequestDialog.value = false;
  }
}

async function runAlgorithm(): Promise<void> {
  // Keep selection state in sync with the grid at click time.
  onSelectionChanged();

  if (selectedPOs.value.length < 2) return;

  if (!queueStore.canSubmit) {
    toast.warning(
      "Too many running requests. Please wait for one to complete."
    );
    return;
  }

  if (
    totalSelectedItems.value > LARGE_ITEM_SELECTION_THRESHOLD &&
    !globalThis.confirm(
      `Your ${selectedPOs.value.length.toString()} selected POs contain ${totalSelectedItems.value.toLocaleString()} items. The algorithm may take a long time to complete. Continue?`
    )
  ) {
    return;
  }

  runState.isRunning = true;
  runState.phase = "preparing-request";
  runState.error = undefined;
  setTraceLog(undefined);

  try {
    const requestPayload = await buildRedistributionRequestPayload();
    const requestBody = JSON.stringify(
      toRedistributionRequestBody(requestPayload),
      null,
      2
    );
    setLastCustomRequestBody(requestBody);

    runState.phase = "running-algorithm";
    const submission = await runRedistributionAlgorithm(
      requestPayload.purchaseOrders,
      requestPayload.rateOfSaleDays,
      requestPayload.poCube,
      requestPayload.algorithm,
      requestPayload.allowedReasons,
      requestPayload.avgDaysOfStockBySku,
      requestPayload.demandSource
    );

    // Enqueue in the UI — polling (redistributionPolling.ts) will notify when it completes
    queueStore.enqueue(
      submission.requestId,
      submission.submittedAt,
      requestPayload.purchaseOrders,
      requestBody
    );
    toast.info("Redistribution submitted", {
      description: "You'll be notified when results are ready.",
    });
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : "An unexpected error occurred.";
    runState.error = errorMessage;
    toast.error(`Algorithm submission failed: ${errorMessage}`);
  } finally {
    runState.phase = "idle";
    runState.isRunning = false;
  }
}
</script>

<template>
  <div
    class="panel-card results-panel-card flex h-full min-h-0 flex-col overflow-y-auto"
  >
    <div class="panel-card-header results-panel-header !py-2.5">
      <h2 class="m-0">
        📋 Open Purchase Orders
        <span
          v-if="searchResults !== undefined && !tooManyVendors && !searching"
          class="font-medium text-slate-500"
          >—
          {{ headerSearchSummaryLabel }}
        </span>
      </h2>
      <div class="results-panel-controls ml-auto flex items-center gap-3">
        <div v-if="isTestMode" class="flex flex-none items-center gap-2">
          <span class="form-field-label">Test Scenario</span>
          <div class="w-[25vw] min-w-[180px]">
            <Dropdown
              v-model="selectedTestFile"
              :options="testScenarioOptions"
              placeholder="Load test data..."
              :loading="testDataLoading"
              :show-group-headers="true"
              :get-group-by="getTestScenarioCategory"
            />
          </div>
          <button
            class="test-scenario-help-btn"
            type="button"
            :disabled="!selectedTestFile || testDocLoading"
            aria-label="View test scenario documentation"
            @click="showTestScenarioDoc"
          >
            ?
          </button>
        </div>
        <div class="flex items-center gap-2">
          <span class="form-field-label whitespace-nowrap">{{
            rateOfSaleFieldLabel
          }}</span>
          <div class="w-36">
            <Dropdown
              v-model="rateOfSale"
              :options="ROS_OPTIONS"
              placeholder="Select..."
            />
          </div>
        </div>

        <button
          class="panel-secondary-button"
          type="button"
          :disabled="rowData.length === 0"
          @click="toggleAllPOs"
        >
          Select / Deselect All
        </button>
      </div>
    </div>

    <div class="results-panel-meta-strip">
      <div class="results-panel-meta-left">
        <span
          class="results-panel-section-chip results-panel-section-chip--primary"
        >
          {{ primaryResultSetChipLabel }}: {{ totalPOsFound }}
        </span>
        <span
          v-if="hasRelatedRows"
          class="results-panel-section-chip results-panel-section-chip--related"
          title="Purchase orders related by request ID"
        >
          Related PO Results: {{ totalRelatedFound }}
        </span>
      </div>

      <div
        v-if="hasFilterToggle"
        class="results-panel-filter-toggle"
        role="group"
        aria-label="Filter result sets"
      >
        <span class="results-panel-filter-label">View:</span>
        <button
          type="button"
          class="results-panel-filter-button"
          :class="{ 'is-active': resultsFilter === 'all' }"
          :aria-pressed="resultsFilter === 'all'"
          @click="resultsFilter = 'all'"
        >
          All
        </button>
        <button
          type="button"
          class="results-panel-filter-button"
          :class="{ 'is-active': resultsFilter === 'cutting-date' }"
          :aria-pressed="resultsFilter === 'cutting-date'"
          @click="resultsFilter = 'cutting-date'"
        >
          Cutting Date
        </button>
        <button
          type="button"
          class="results-panel-filter-button"
          :class="{ 'is-active': resultsFilter === 'related' }"
          :aria-pressed="resultsFilter === 'related'"
          @click="resultsFilter = 'related'"
        >
          Related
        </button>
      </div>
    </div>

    <div
      v-if="searchResults === undefined"
      class="results-panel-guidance"
      role="status"
      aria-live="polite"
    >
      <p class="results-panel-guidance__title">Before you start</p>
      <p class="results-panel-guidance__body">
        Run a search to populate the results grid, then select 2 or more
        Purchase Orders to enable redistribution.
      </p>
    </div>

    <!-- Grid with rows -->
    <div class="min-h-[200px] min-w-0 flex-1 overflow-hidden">
      <div
        v-if="showInlineSearchLoadingBanner"
        class="results-panel-loading-banner"
        role="status"
        aria-live="polite"
      >
        ⏳ {{ inlineSearchLoadingMessage }}
      </div>
      <AppGrid
        id="search-results-grid"
        :column-defs="columnDefs"
        :row-data="rowData"
        :default-col-def="defaultColDef"
        :selection-column-def="selectionColumnDef"
        :row-selection="rowSelection"
        :is-full-width-row="isFullWidthRow"
        :full-width-cell-renderer="PoDetailRenderer"
        :get-row-id="getRowId"
        :get-row-height="getRowHeight"
        :default-row-height="ROW_HEIGHT"
        :fill-container="true"
        :post-sort-rows="postSortRows"
        :no-rows-overlay-component-params="{
          showLoadingAnimation: searching,
          contextMessage: gridLoadingMessage,
          getTitle: () => {
            return searching === true
              ? `Searching ${activeFilterLabel}...`
              : searchResults === undefined
                ? 'Run a search to populate results.'
                : 'No POs Found';
          },
          getSubtitle: () => {
            return searching === true
              ? ''
              : searchResults === undefined
                ? 'Select 2 or more Purchase Orders to run redistribution.'
                : (activeSearchNoResultsMessage ??
                  'Try adjusting your search criteria or date range.');
          },
        }"
        @grid-ready="onGridReady"
        @selection-changed="onSelectionChanged"
        @sort-changed="onSortChanged"
      />
      <div
        v-if="showRelatedFeedback"
        class="related-purchase-orders-feedback rounded-xl border px-4 py-3"
        :class="
          relatedFeedbackTone === 'error'
            ? 'border-amber-300 bg-amber-50 text-amber-900'
            : 'border-slate-200 bg-slate-50 text-slate-700'
        "
        :role="relatedFeedbackRole"
        aria-live="polite"
      >
        <p class="text-sm font-semibold text-[#1e3f73]">
          Related Purchase Orders
        </p>
        <p class="mt-1 text-sm">{{ poSearchResultsContext.helperMessage }}</p>
      </div>
    </div>

    <div
      class="results-panel-redistribution-hint"
      role="status"
      aria-live="polite"
    >
      {{
        selectedPOs.length >= 2
          ? `Ready to run redistribution for ${selectedPOs.length} selected Purchase Order(s).`
          : "Select at least 2 Purchase Orders to run redistribution."
      }}
    </div>

    <!-- Algorithm error -->
    <div v-if="algorithmError" class="results-panel-error-banner" role="alert">
      ⚠️ {{ algorithmError }}
    </div>

    <!-- Selection bar -->
    <div class="results-panel-selection-bar">
      <SelectedPurchaseOrdersPanel
        :selected-pos="selectedPOs"
        :total-selected-items="totalSelectedItems"
        @remove-po="removeFromSelection"
      />
      <div class="flex items-end gap-4">
        <!-- Algorithm Options checkboxes (hidden — BalanceWos always enabled) -->
        <div v-if="false" class="flex flex-col gap-1">
          <span class="form-field-label">Algorithm Options</span>
          <div
            class="flex flex-col gap-1 rounded-md border border-slate-300 bg-white px-3 py-2"
          >
            <label
              for="opt-critical-risk"
              class="flex cursor-pointer items-center gap-2 text-sm text-slate-600"
              title="Alleviates CRITICAL stockout risks (where possible)"
            >
              <input
                id="opt-critical-risk"
                :checked="algorithmOptions.criticalRisk"
                type="checkbox"
                class="accent-[#1e3f73]"
                @change="
                  updateAlgorithmOption(
                    'criticalRisk',
                    ($event.target as HTMLInputElement).checked
                  )
                "
              />
              Prevent Critical Stockout Risk
            </label>
            <label
              for="opt-moderate-risk"
              class="flex cursor-pointer items-center gap-2 text-sm text-slate-600"
              title="Alleviates CRITICAL and MODERATE stockout risks (where possible)"
            >
              <input
                id="opt-moderate-risk"
                :checked="algorithmOptions.moderateRisk"
                type="checkbox"
                class="accent-[#1e3f73]"
                @change="
                  updateAlgorithmOption(
                    'moderateRisk',
                    ($event.target as HTMLInputElement).checked
                  )
                "
              />
              Prevent Moderate Stockout Risk
            </label>
            <label
              for="opt-balance-wos"
              class="flex cursor-pointer items-center gap-2 text-sm text-slate-600"
              title="Attempts to Balance SKU WOS across selected PO Warehouses"
            >
              <input
                id="opt-balance-wos"
                :checked="algorithmOptions.balanceWos"
                type="checkbox"
                class="accent-[#1e3f73]"
                @change="
                  updateAlgorithmOption(
                    'balanceWos',
                    ($event.target as HTMLInputElement).checked
                  )
                "
              />
              Balance WOS
            </label>
          </div>
        </div>

        <FormField label="PO Cube Size">
          <Textbox v-model="poCube" placeholder="2300" label="PO Cube Size" />
        </FormField>

        <FormField label="Demand">
          <Dropdown
            v-model="demandSource"
            :options="[...demandSourceOptions]"
            placeholder="Select demand source"
          />
        </FormField>

        <FormField label="Algorithm">
          <Dropdown
            v-model="algorithmType"
            :options="[...ALGORITHM_OPTIONS]"
            placeholder="Select algorithm"
          />
        </FormField>

        <RedistributionRunActions
          :is-test-mode="isTestMode"
          :is-running="isRunning"
          :is-preparing-custom-request-dialog="isPreparingCustomRequestDialog"
          :selected-po-count="selectedPOs.length"
          :run-calculation-status="runCalculationStatus"
          @run-algorithm="runAlgorithm"
          @open-custom-request-body-dialog="void openCustomRequestBodyDialog()"
        />
      </div>
    </div>
  </div>
</template>
